import React from "react";
import SafeImage from "../../components/shared/SafeImage";
import MediaFallback from "../../assets/images/dummy-image.png";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormText,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  getContestPartners,
  createContestPartner,
  updateContestPartner,
  deleteContestPartner,
} from "../../api/contest-partners";
import { uploadViaPresign } from "../../api/upload";

// `setLoading` / `setTitlePage` arrive as PROPS from routes/privateRoutes.js
// (which owns that state), not from redux — same as every other container.

/** Pull a displayable string out of an API error body (never render the object). */
const errorMessage = (body) => {
  if (typeof body === "string") return body;
  if (body && typeof body.message === "string") return body.message;
  return "Something went wrong. Please try again.";
};

const EMPTY_FORM = {
  id: null,
  name: "",
  logo: null,
  thumbnail: null,
  videoPath: null,
  status: true,
  sortOrder: 0,
};

/**
 * Contest Partners (sponsors) management — the missing Admin Site half of a
 * feature whose API has existed since 5.0.8.
 *
 * This is what actually puts a sponsor's promo video in the app's Contest tab.
 * The client reasonably assumed the Headline form did that (it is the other
 * form with a video field), but headlines are news items and never reach the
 * Contest tab — so no amount of fixing Headline could have surfaced a partner.
 *
 * Media goes through the presigned-URL flow; the form stores the returned S3
 * keys and the API resolves them to absolute URLs on read.
 */
class ContestPartners extends React.Component {
  state = {
    partners: [],
    form: { ...EMPTY_FORM },
    isModalOpen: false,
    confirmDeleteId: null,
    saving: false,
    uploading: null,
  };

  async componentDidMount() {
    this.props.setTitlePage("Contest Partners");
    await this.load();
  }

  load = async () => {
    this.props.setLoading(true);
    const res = await this.props.getContestPartners();
    if (res && res.status === 200) {
      this.setState({ partners: res.data || [] });
    } else if (res && res.data) {
      toast(errorMessage(res.data), { type: "error" });
    }
    this.props.setLoading(false);
  };

  openCreate = () => this.setState({ form: { ...EMPTY_FORM }, isModalOpen: true });

  openEdit = (row) =>
    this.setState({
      isModalOpen: true,
      form: {
        id: row.id,
        name: row.name || "",
        // The list returns resolved absolute URLs; the form only re-sends a key
        // when a NEW file is picked, so unchanged media is left alone.
        logo: null,
        thumbnail: null,
        videoPath: null,
        status: !!row.status,
        sortOrder: row.sortOrder || 0,
      },
    });

  close = () => this.setState({ isModalOpen: false, form: { ...EMPTY_FORM } });

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((s) => ({
      form: { ...s.form, [name]: type === "checkbox" ? checked : value },
    }));
  };

  handleFile = async (e, field, kind) => {
    const file = e.target.files[0];
    if (!file) return;
    this.setState({ uploading: field });
    try {
      const key = await uploadViaPresign(file, kind);
      this.setState((s) => ({ form: { ...s.form, [field]: key } }));
      toast("Uploaded", { type: "success" });
    } catch (error) {
      toast(errorMessage(error && error.message), { type: "error" });
    } finally {
      this.setState({ uploading: null });
    }
  };

  save = async () => {
    const { form } = this.state;
    if (!form.name.trim()) {
      toast("Please enter a partner name!", { type: "error" });
      return;
    }
    // Only send media fields that were actually set — omitting a field leaves
    // the stored value untouched on edit (the DTO is `.partial()`).
    const payload = {
      name: form.name.trim(),
      status: !!form.status,
      sortOrder: Number(form.sortOrder) || 0,
    };
    if (form.logo) payload.logo = form.logo;
    if (form.thumbnail) payload.thumbnail = form.thumbnail;
    if (form.videoPath) payload.videoPath = form.videoPath;

    this.setState({ saving: true });
    const res = form.id
      ? await this.props.updateContestPartner(form.id, payload)
      : await this.props.createContestPartner(payload);
    this.setState({ saving: false });

    if (res && (res.status === 200 || res.status === 201)) {
      toast(form.id ? "Partner updated" : "Partner created", { type: "success" });
      this.close();
      await this.load();
    } else if (res && res.data) {
      toast(errorMessage(res.data), { type: "error" });
    }
  };

  remove = async () => {
    const id = this.state.confirmDeleteId;
    this.setState({ confirmDeleteId: null });
    const res = await this.props.deleteContestPartner(id);
    if (res && (res.status === 200 || res.status === 204)) {
      toast("Partner deleted", { type: "success" });
      await this.load();
    } else if (res && res.data) {
      toast(errorMessage(res.data), { type: "error" });
    }
  };

  columns = [
    {
      name: "Logo",
      cell: (row) =>
        row.logoUrl ? (
          <SafeImage fallback={MediaFallback} src={row.logoUrl} alt={row.name} style={{ height: 40, borderRadius: 4 }} />
        ) : (
          <span className="text-muted">—</span>
        ),
      width: "90px",
    },
    { name: "Name", selector: (row) => row.name, sortable: true },
    {
      name: "Video",
      cell: (row) =>
        row.videoUrl ? (
          <a href={row.videoUrl} target="_blank" rel="noopener noreferrer">
            View video
          </a>
        ) : (
          // Without a video the card still lists in the app but has nothing to
          // play — worth flagging, since the video is the point of a partner.
          <span style={{ color: "#c00" }}>No video</span>
        ),
    },
    {
      name: "Shown in app",
      cell: (row) => (row.status ? "Yes" : "No"),
      width: "120px",
    },
    { name: "Order", selector: (row) => row.sortOrder, width: "90px" },
    {
      name: "Actions",
      // `nowrap` + auto width for the same reason as Add Partner above: without
      // it the admin's global button rule wrapped "Delete" onto two lines.
      cell: (row) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            color="primary"
            size="sm"
            onClick={() => this.openEdit(row)}
            style={{ whiteSpace: "nowrap", width: "auto", display: "inline-block" }}
          >
            Edit
          </Button>
          <Button
            color="danger"
            size="sm"
            onClick={() => this.setState({ confirmDeleteId: row.id })}
            style={{ whiteSpace: "nowrap", width: "auto", display: "inline-block" }}
          >
            Delete
          </Button>
        </div>
      ),
      width: "180px",
    },
  ];

  render() {
    const { partners, form, isModalOpen, saving, uploading, confirmDeleteId } = this.state;
    return (
      <div className="main-scroll">
        {/* Not the shared `header-song` layout: that flexes its children, which
            stretched the button across the whole row. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div>
            <h3 className="title" style={{ marginBottom: "4px" }}>
              Contest Partners
            </h3>
            <p className="text-muted" style={{ margin: 0 }}>
              Sponsors shown in the app&apos;s Contest tab. Each partner&apos;s promo
              video plays when a user taps their card.
            </p>
          </div>
          {/* An admin-wide stylesheet rule makes buttons `display:block`, which
              stretches them inside a flex row — pin this one to its content. */}
          <Button
            color="primary"
            onClick={this.openCreate}
            style={{ whiteSpace: "nowrap", flex: "0 0 auto", width: "auto", display: "inline-block" }}
          >
            Add Partner
          </Button>
        </div>

        <DataTable
          columns={this.columns}
          data={partners}
          noDataComponent="No contest partners yet — the app's Contest Partners section is empty until you add one."
        />

        <Modal isOpen={isModalOpen} toggle={this.close}>
          <ModalHeader toggle={this.close}>
            {form.id ? "Edit Partner" : "Add Partner"}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="name">Partner Name*</Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  placeholder="Enter partner name"
                  onChange={this.handleChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Logo</Label>
                <Input
                  type="file"
                  accept="image/*"
                  disabled={uploading === "logo"}
                  onChange={(e) => this.handleFile(e, "logo", "profile")}
                />
                {uploading === "logo" && <FormText>Uploading…</FormText>}
                {form.logo && <FormText>Uploaded ✓</FormText>}
              </FormGroup>

              <FormGroup>
                <Label>Video Thumbnail</Label>
                <Input
                  type="file"
                  accept="image/*"
                  disabled={uploading === "thumbnail"}
                  onChange={(e) => this.handleFile(e, "thumbnail", "poster")}
                />
                {uploading === "thumbnail" && <FormText>Uploading…</FormText>}
                {form.thumbnail && <FormText>Uploaded ✓</FormText>}
              </FormGroup>

              <FormGroup>
                <Label>Promo Video</Label>
                <Input
                  type="file"
                  accept="video/*"
                  disabled={uploading === "videoPath"}
                  onChange={(e) => this.handleFile(e, "videoPath", "video")}
                />
                {uploading === "videoPath" && <FormText>Uploading…</FormText>}
                {form.videoPath && <FormText>Uploaded ✓</FormText>}
                {form.id && !form.videoPath && (
                  <FormText>Leave empty to keep the current video.</FormText>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="sortOrder">Sort Order</Label>
                <Input
                  id="sortOrder"
                  name="sortOrder"
                  type="number"
                  value={form.sortOrder}
                  onChange={this.handleChange}
                />
              </FormGroup>

              <FormGroup check>
                <Label check>
                  <Input
                    type="checkbox"
                    name="status"
                    checked={!!form.status}
                    onChange={this.handleChange}
                  />{" "}
                  Show in the app
                </Label>
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.close}>
              Cancel
            </Button>
            <Button color="primary" onClick={this.save} disabled={saving || !!uploading}>
              {saving ? "Saving..." : form.id ? "Save" : "Create"}
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={confirmDeleteId !== null} toggle={() => this.setState({ confirmDeleteId: null })}>
          <ModalHeader>Delete partner</ModalHeader>
          <ModalBody>
            This removes the partner from the app's Contest tab. Continue?
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.setState({ confirmDeleteId: null })}>
              Cancel
            </Button>
            <Button color="danger" onClick={this.remove}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (store) => ({ admin: store.user });

export default connect(mapStateToProps, {
  getContestPartners,
  createContestPartner,
  updateContestPartner,
  deleteContestPartner,
})(withRouter(ContestPartners));
